/**
 * 🎨 Navigation Floating Toolbar - Modern Navigation Settings
 * 
 * Floating Toolbar für Navigation-Einstellungen
 * Inspiriert von ContainerFloatingToolbar & ButtonFloatingToolbar
 * 
 * Features:
 * - Logo Upload & Dimensions
 * - Navigation Links (Add, Edit, Delete, Reorder)
 * - Social Media Icons
 * - CTA Button
 * - Styling (Colors, Heights, etc.)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import './NavigationFloatingToolbar.css';

function NavigationFloatingToolbar({
    isVisible,
    navigationData,
    onUpdate,
    onClose,
    colors = {},
    spaceFonts = {},
    isMobileView = false,
    navRef,
    onLogoUpload,
    isWidgetEditCollapsed = false,
    isAddWidgetsCollapsed = false
}) {
    // 🎨 Toolbar State
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const toolbarRef = useRef(null);
    
    // 🎯 Dragging State - wie Button Toolbar
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Font Context für Custom Fonts
    const fonts = useFont();
    
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); // 'logo', 'links', 'social', 'cta', 'styling', 'textFont', 'ctaFont', 'mobile'
    
    // 🎯 Link Edit State
    const [editingLinkId, setEditingLinkId] = useState(null);
    
    // 🎯 State für Desktop/Mobile Toggle in Mobile Section  
    const [mobileDevice, setMobileDevice] = useState(isMobileView ? 'mobile' : 'desktop');
    
    // 🎯 Synchronisiere Toggle mit isMobileView
    useEffect(() => {
        setMobileDevice(isMobileView ? 'mobile' : 'desktop');
    }, [isMobileView]);
    
    // 🎯 Drag Handlers - MUSS VOR early return stehen! - wie Button Toolbar
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking - Toolbar bleibt im Viewport
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 300);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 200);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true); // ✅ Mark as manually positioned
    };
    
    // ✅ Drag Event Listeners - MUSS VOR early return stehen!
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // ✅ Sync showToolbar with isVisible prop
    useEffect(() => {
        if (isVisible) {
            setShowToolbar(true);
            updateToolbarPosition();
        } else {
            setShowToolbar(false);
            setExpandedSection(null);
        }
    }, [isVisible]);
    
    // ✅ Update Toolbar Position - UNTER der Navigation
    const updateToolbarPosition = () => {
        // ✅ FREEZE POSITION: Wenn expandedSection aktiv ist ODER manuell positioniert
        if (expandedSection || isManuallyPositioned) {
            setShowToolbar(true);
            return;
        }
        
        if (!navRef || !navRef.current) {
            // Fallback: Fixed position - zentriert im page-preview Container
            const top = 180;
            
            // ✅ CRITICAL FIX: Berechne Offset basierend auf BEIDEN Sidebars
            // Rechte Sidebar (Widget Edit): Geschlossen = +110px (mehr Platz rechts)
            const rightOffset = isWidgetEditCollapsed ? 110 : 0;
            // Linke Sidebar (Add Widgets): Geschlossen = -110px (mehr Platz links)
            const leftOffset = isAddWidgetsCollapsed ? -110 : 0;
            // Total Offset = Kombination beider
            const totalOffset = rightOffset + leftOffset;
            
            // ✅ CRITICAL FIX: Finde page-preview Container für korrekte Position
            const previewContainer = document.querySelector('.page-preview') || document.querySelector('.page_preview');
            if (previewContainer) {
                const previewRect = previewContainer.getBoundingClientRect();
                const left = previewRect.left + (previewRect.width / 2) + totalOffset;
                setToolbarPosition({ top, left });
            } else {
                // Ultimate Fallback
                const left = (window.innerWidth / 2) + totalOffset;
                setToolbarPosition({ top, left });
            }
            return;
        }
        
        // ✅ Position UNTER der Navigation berechnen
        const navRect = navRef.current.getBoundingClientRect();
        const top = navRect.bottom + window.scrollY + 20; // 20px Abstand unter der Navigation
        
        // ✅ CRITICAL FIX: Berechne Offset basierend auf BEIDEN Sidebars!
        // 4 Cases:
        // 1. Beide offen (300px + 300px) → Offset = 0 + 0 = 0
        // 2. Beide geschlossen (40px + 40px) → Offset = +110 + (-110) = 0
        // 3. Links offen, Rechts geschlossen (300px + 40px) → Offset = +110 + 0 = +110
        // 4. Links geschlossen, Rechts offen (40px + 300px) → Offset = 0 + (-110) = -110
        
        // Rechte Sidebar (Widget Edit): Geschlossen = +110px (Preview verschiebt sich nach rechts)
        const rightOffset = isWidgetEditCollapsed ? 110 : 0;
        // Linke Sidebar (Add Widgets): Geschlossen = -110px (Preview verschiebt sich nach links)
        const leftOffset = isAddWidgetsCollapsed ? -110 : 0;
        // Total Offset = Kombination beider
        const totalOffset = rightOffset + leftOffset;
        
        // Horizontale Position: Mitte der Navigation + Total Offset
        const left = navRect.left + (navRect.width / 2) + totalOffset;
        
        console.log('📍 [NavigationToolbar] Position berechnet:', {
            navLeft: navRect.left,
            navWidth: navRect.width,
            isWidgetEditCollapsed,
            isAddWidgetsCollapsed,
            rightOffset,
            leftOffset,
            totalOffset,
            calculatedLeft: left,
            top
        });
        
        setToolbarPosition({ top, left });
    };
    
    // ✅ CRITICAL FIX: Update Position when Sidebar Toggle Changes
    useEffect(() => {
        console.log('🔄 [NavigationToolbar] Sidebar Toggle Changed:', {
            isWidgetEditCollapsed,
            isAddWidgetsCollapsed,
            showToolbar,
            isManuallyPositioned
        });
        
        if (showToolbar && !isManuallyPositioned) {
            // Position neu berechnen wenn EINE der Sidebars auf/zu geht
            console.log('✅ [NavigationToolbar] Updating position after sidebar toggle...');
            updateToolbarPosition();
        }
    }, [isWidgetEditCollapsed, isAddWidgetsCollapsed]); // Trigger bei BEIDEN Sidebar Toggles
    
    // ✅ Update Position on Resize & Scroll - nur wenn NICHT manuell positioniert
    useEffect(() => {
        if (!showToolbar || isManuallyPositioned) return;
        
        const handleResizeScroll = () => {
            if (!expandedSection) {
                updateToolbarPosition();
            }
        };
        
        window.addEventListener('resize', handleResizeScroll);
        window.addEventListener('scroll', handleResizeScroll, true);
        
        return () => {
            window.removeEventListener('resize', handleResizeScroll);
            window.removeEventListener('scroll', handleResizeScroll, true);
        };
    }, [showToolbar, expandedSection, isManuallyPositioned]);
    
    // ✅ Handle Outside Click - Close toolbar
    useEffect(() => {
        if (!showToolbar || !isVisible) return;
        
        const handleClickOutside = (event) => {
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            const clickedInNav = navRef && navRef.current && navRef.current.contains(event.target);
            
            if (!clickedInToolbar && !clickedInNav) {
                setExpandedSection(null);
                if (onClose) {
                    onClose();
                }
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolbar, isVisible, onClose]);
    
    // 🚫 Don't render if not visible
    if (!isVisible || !showToolbar) {
        return null;
    }
    
    // 🎨 Helper: Add New Link
    const handleAddLink = () => {
        const newLink = {
            id: `link-${Date.now()}`,
            text: 'New Link',
            url: '#',
            visible: true
        };
        
        onUpdate({
            navLinks: [...navigationData.navLinks, newLink]
        });
    };
    
    // 🎨 Helper: Update Link
    const handleUpdateLink = (linkId, updates) => {
        const updatedLinks = navigationData.navLinks.map(link =>
            link.id === linkId ? { ...link, ...updates } : link
        );
        
        onUpdate({ navLinks: updatedLinks });
    };
    
    // 🎨 Helper: Delete Link
    const handleDeleteLink = (linkId) => {
        const updatedLinks = navigationData.navLinks.filter(link => link.id !== linkId);
        onUpdate({ navLinks: updatedLinks });
    };
    
    // 🎨 Helper: Move Link Up/Down
    const handleMoveLink = (linkId, direction) => {
        const currentIndex = navigationData.navLinks.findIndex(link => link.id === linkId);
        if (currentIndex === -1) return;
        
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= navigationData.navLinks.length) return;
        
        const newLinks = [...navigationData.navLinks];
        [newLinks[currentIndex], newLinks[newIndex]] = [newLinks[newIndex], newLinks[currentIndex]];
        
        onUpdate({ navLinks: newLinks });
    };
    
    // 🎨 Helper: Update Social Icon
    const handleUpdateSocialIcon = (iconId, updates) => {
        const updatedIcons = navigationData.socialIcons.map(icon =>
            icon.id === iconId ? { ...icon, ...updates } : icon
        );
        
        onUpdate({ socialIcons: updatedIcons });
    };
    
    // 🎨 Helper: Handle Logo Upload
    const handleLogoUploadClick = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;
            
            try {
                const downloadURL = await onLogoUpload(file);
                onUpdate({ logo: downloadURL });
            } catch (error) {
                console.error('Error uploading logo:', error);
                alert('Upload failed. Please try again.');
            }
        };
        input.click();
    };
    
    // 🎨 Toolbar Content (wird über Portal gerendert)
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`nav-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div className="nav-toolbar">
                {/* 🎯 MAIN TOOLBAR - Immer sichtbar */}
                <div className="nav-toolbar-main">
                    {/* 🎯 Drag Handle Icon - wie Button Toolbar */}
                    <div 
                        className="nav-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            // ✅ FIX: Verhindere dass Click-Event die Toolbar schließt
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* Logo */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'logo' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'logo' ? null : 'logo')}
                        title="Logo Settings"
                    >
                        <i className="fas fa-image"></i>
                    </button>
                    
                    {/* Links */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'links' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'links' ? null : 'links')}
                        title="Navigation Links"
                    >
                        <i className="fas fa-link"></i>
                    </button>
                    
                    {/* Social */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'social' ? 'active' : ''} ${navigationData.socialIconsVisible ? 'has-feature' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'social' ? null : 'social')}
                        title="Social Media Icons"
                    >
                        <i className="fas fa-share-alt"></i>
                    </button>
                    
                    {/* CTA Button */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'cta' ? 'active' : ''} ${navigationData.ctaButton?.enabled ? 'has-feature' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'cta' ? null : 'cta')}
                        title="CTA Button"
                    >
                        <i className="fas fa-mouse-pointer"></i>
                    </button>
                    
                    {/* Text Font */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'textFont' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'textFont' ? null : 'textFont')}
                        title="Text Font (Navigation Links)"
                    >
                        <i className="fas fa-font"></i>
                    </button>
                    
                    {/* CTA Font */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'ctaFont' ? 'active' : ''} ${navigationData.ctaButton?.enabled ? 'has-feature' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'ctaFont' ? null : 'ctaFont')}
                        title="CTA Button Font"
                    >
                        <span style={{ fontSize: '18px', fontWeight: '700' }}>Aa</span>
                    </button>
                    
                    {/* Styling */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'styling' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'styling' ? null : 'styling')}
                        title="Styling"
                    >
                        <i className="fas fa-palette"></i>
                    </button>
                    
                    {/* Mobile Settings */}
                    <button
                        className={`nav-toolbar-btn ${expandedSection === 'mobile' ? 'active' : ''}`}
                        onClick={() => setExpandedSection(expandedSection === 'mobile' ? null : 'mobile')}
                        title="Mobile Settings"
                    >
                        <i className="fas fa-mobile-alt"></i>
                    </button>
                    
                    {/* Divider */}
                    <div className="nav-toolbar-divider"></div>
                    
                    {/* Close */}
                    <button
                        className="nav-toolbar-btn nav-toolbar-btn-close"
                        onClick={() => {
                            setExpandedSection(null);
                            if (onClose) onClose();
                        }}
                        title="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                
                {/* 🎨 EXPANDABLE SECTIONS */}
                
                {/* 1️⃣ LOGO SECTION */}
                {expandedSection === 'logo' && (
                    <div className="nav-expanded-section">
                        {/* Logo Position */}
                        <div className="nav-position-group">
                            <span className="nav-color-label">Position</span>
                            <div className="nav-position-buttons">
                                <button
                                    className={`nav-position-btn ${navigationData.logoPosition === 'left' || !navigationData.logoPosition ? 'active' : ''}`}
                                    onClick={() => onUpdate({ logoPosition: 'left' })}
                                    title="Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                    <span>Left</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.logoPosition === 'center' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ logoPosition: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.logoPosition === 'right' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ logoPosition: 'right' })}
                                    title="Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                    <span>Right</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Logo Upload */}
                        <div className="nav-logo-upload">
                            {navigationData.logo && (
                                <div className="nav-logo-preview">
                                    <img src={navigationData.logo} alt="Logo" />
                                    <button
                                        className="nav-logo-remove"
                                        onClick={() => onUpdate({ logo: '' })}
                                        title="Remove Logo"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            )}
                            
                            <button
                                className="nav-upload-btn"
                                onClick={handleLogoUploadClick}
                            >
                                <i className="fas fa-upload"></i>
                                <span>{navigationData.logo ? 'Change Logo' : 'Upload Logo'}</span>
                            </button>
                        </div>
                        
                        {/* Logo Dimensions */}
                        {navigationData.logo && (
                            <>
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Width ({isMobileView ? 'Mobile' : 'Desktop'})
                                        <span className="nav-slider-value">
                                            {isMobileView ? navigationData.logoWidthMobile : navigationData.logoWidth}px
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="50"
                                        max="300"
                                        value={isMobileView ? navigationData.logoWidthMobile : navigationData.logoWidth}
                                        onChange={(e) => {
                                            const key = isMobileView ? 'logoWidthMobile' : 'logoWidth';
                                            onUpdate({ [key]: parseInt(e.target.value) });
                                        }}
                                        className="nav-slider"
                                    />
                                </div>
                                
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Height ({isMobileView ? 'Mobile' : 'Desktop'})
                                        <span className="nav-slider-value">
                                            {isMobileView ? navigationData.logoHeightMobile : navigationData.logoHeight}px
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="30"
                                        max="150"
                                        value={isMobileView ? navigationData.logoHeightMobile : navigationData.logoHeight}
                                        onChange={(e) => {
                                            const key = isMobileView ? 'logoHeightMobile' : 'logoHeight';
                                            onUpdate({ [key]: parseInt(e.target.value) });
                                        }}
                                        className="nav-slider"
                                    />
                                </div>
                            </>
                        )}
                        
                        <span className="nav-section-label">Logo</span>
                    </div>
                )}
                
                {/* 2️⃣ LINKS SECTION */}
                {expandedSection === 'links' && (
                    <div className="nav-expanded-section">
                        {/* Links Position */}
                        <div className="nav-position-group">
                            <span className="nav-color-label">Position</span>
                            <div className="nav-position-buttons">
                                <button
                                    className={`nav-position-btn ${navigationData.navLinksPosition === 'left' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ navLinksPosition: 'left' })}
                                    title="Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                    <span>Left</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.navLinksPosition === 'center' || !navigationData.navLinksPosition ? 'active' : ''}`}
                                    onClick={() => onUpdate({ navLinksPosition: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.navLinksPosition === 'right' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ navLinksPosition: 'right' })}
                                    title="Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                    <span>Right</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Toggle Links Visibility */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={navigationData.navLinksVisible || false}
                                    onChange={(e) => onUpdate({ navLinksVisible: e.target.checked })}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Show Navigation Links</span>
                            </label>
                        </div>
                        
                        {navigationData.navLinksVisible && (
                            <>
                                {/* Links List */}
                                <div className="nav-links-list">
                                    {navigationData.navLinks.map((link, index) => (
                                        <div key={link.id} className="nav-link-item">
                                            {editingLinkId === link.id ? (
                                                // Edit Mode
                                                <div className="nav-link-edit">
                                                    <input
                                                        type="text"
                                                        className="nav-link-input"
                                                        value={link.text}
                                                        onChange={(e) => handleUpdateLink(link.id, { text: e.target.value })}
                                                        placeholder="Link Text"
                                                    />
                                                    <input
                                                        type="text"
                                                        className="nav-link-input"
                                                        value={link.url}
                                                        onChange={(e) => handleUpdateLink(link.id, { url: e.target.value })}
                                                        placeholder="URL (e.g., #home)"
                                                    />
                                                    <button
                                                        className="nav-link-done-btn"
                                                        onClick={() => setEditingLinkId(null)}
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                // View Mode
                                                <div className="nav-link-view">
                                                    <div className="nav-link-info">
                                                        <span className="nav-link-text">{link.text}</span>
                                                        <span className="nav-link-url">{link.url}</span>
                                                    </div>
                                                    <div className="nav-link-controls">
                                                        {/* Visible Toggle */}
                                                        <button
                                                            className={`nav-link-control-btn ${link.visible ? 'active' : ''}`}
                                                            onClick={() => handleUpdateLink(link.id, { visible: !link.visible })}
                                                            title={link.visible ? 'Hide' : 'Show'}
                                                        >
                                                            <i className={`fas fa-eye${link.visible ? '' : '-slash'}`}></i>
                                                        </button>
                                                        
                                                        {/* Move Up */}
                                                        <button
                                                            className="nav-link-control-btn"
                                                            onClick={() => handleMoveLink(link.id, 'up')}
                                                            disabled={index === 0}
                                                            title="Move Up"
                                                        >
                                                            <i className="fas fa-arrow-up"></i>
                                                        </button>
                                                        
                                                        {/* Move Down */}
                                                        <button
                                                            className="nav-link-control-btn"
                                                            onClick={() => handleMoveLink(link.id, 'down')}
                                                            disabled={index === navigationData.navLinks.length - 1}
                                                            title="Move Down"
                                                        >
                                                            <i className="fas fa-arrow-down"></i>
                                                        </button>
                                                        
                                                        {/* Edit */}
                                                        <button
                                                            className="nav-link-control-btn"
                                                            onClick={() => setEditingLinkId(link.id)}
                                                            title="Edit"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        
                                                        {/* Delete */}
                                                        <button
                                                            className="nav-link-control-btn nav-link-delete-btn"
                                                            onClick={() => handleDeleteLink(link.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Add Link Button */}
                                <button
                                    className="nav-add-link-btn"
                                    onClick={handleAddLink}
                                >
                                    <i className="fas fa-plus"></i>
                                    <span>Add Link</span>
                                </button>
                            </>
                        )}
                        
                        <span className="nav-section-label">Navigation Links</span>
                    </div>
                )}
                
                {/* 3️⃣ SOCIAL SECTION */}
                {expandedSection === 'social' && (
                    <div className="nav-expanded-section">
                        {/* Social Icons Position */}
                        <div className="nav-position-group">
                            <span className="nav-color-label">Position</span>
                            <div className="nav-position-buttons">
                                <button
                                    className={`nav-position-btn ${navigationData.socialIconsPosition === 'left' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ socialIconsPosition: 'left' })}
                                    title="Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                    <span>Left</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.socialIconsPosition === 'center' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ socialIconsPosition: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.socialIconsPosition === 'right' || !navigationData.socialIconsPosition ? 'active' : ''}`}
                                    onClick={() => onUpdate({ socialIconsPosition: 'right' })}
                                    title="Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                    <span>Right</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Toggle Social Icons */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={navigationData.socialIconsVisible || false}
                                    onChange={(e) => onUpdate({ socialIconsVisible: e.target.checked })}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Show Social Icons</span>
                            </label>
                        </div>
                        
                        {navigationData.socialIconsVisible && (
                            <div className="nav-social-icons-list">
                                {navigationData.socialIcons.map((icon) => (
                                    <div key={icon.id} className="nav-social-icon-item">
                                        <div className="nav-social-icon-header">
                                            <i className={`fab fa-${icon.platform}`}></i>
                                            <span className="nav-social-icon-name">
                                                {icon.platform.charAt(0).toUpperCase() + icon.platform.slice(1)}
                                            </span>
                                            <label className="nav-social-toggle">
                                                <input
                                                    type="checkbox"
                                                    checked={icon.visible || false}
                                                    onChange={(e) => handleUpdateSocialIcon(icon.id, { visible: e.target.checked })}
                                                />
                                                <span className="nav-social-toggle-slider"></span>
                                            </label>
                                        </div>
                                        {icon.visible && (
                                            <input
                                                type="text"
                                                className="nav-social-url-input"
                                                value={icon.url}
                                                onChange={(e) => handleUpdateSocialIcon(icon.id, { url: e.target.value })}
                                                placeholder={`https://${icon.platform}.com/yourprofile`}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        <span className="nav-section-label">Social Media</span>
                    </div>
                )}
                
                {/* 4️⃣ CTA BUTTON SECTION */}
                {expandedSection === 'cta' && (
                    <div className="nav-expanded-section">
                        {/* CTA Button Position */}
                        <div className="nav-position-group">
                            <span className="nav-color-label">Position</span>
                            <div className="nav-position-buttons">
                                <button
                                    className={`nav-position-btn ${navigationData.ctaButtonPosition === 'left' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ ctaButtonPosition: 'left' })}
                                    title="Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                    <span>Left</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.ctaButtonPosition === 'center' ? 'active' : ''}`}
                                    onClick={() => onUpdate({ ctaButtonPosition: 'center' })}
                                    title="Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    className={`nav-position-btn ${navigationData.ctaButtonPosition === 'right' || !navigationData.ctaButtonPosition ? 'active' : ''}`}
                                    onClick={() => onUpdate({ ctaButtonPosition: 'right' })}
                                    title="Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                    <span>Right</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Toggle CTA */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={navigationData.ctaButton?.enabled || false}
                                    onChange={(e) => onUpdate({ 
                                        ctaButton: { ...navigationData.ctaButton, enabled: e.target.checked }
                                    })}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Enable CTA Button</span>
                            </label>
                        </div>
                        
                        {navigationData.ctaButton?.enabled && (
                            <>
                                {/* CTA Text */}
                                <div className="nav-input-group">
                                    <label className="nav-input-label">Button Text</label>
                                    <input
                                        type="text"
                                        className="nav-input"
                                        value={navigationData.ctaButton.text}
                                        onChange={(e) => onUpdate({
                                            ctaButton: { ...navigationData.ctaButton, text: e.target.value }
                                        })}
                                        placeholder="Get Started"
                                    />
                                </div>
                                
                                {/* CTA URL */}
                                <div className="nav-input-group">
                                    <label className="nav-input-label">Button URL</label>
                                    <input
                                        type="text"
                                        className="nav-input"
                                        value={navigationData.ctaButton.url}
                                        onChange={(e) => onUpdate({
                                            ctaButton: { ...navigationData.ctaButton, url: e.target.value }
                                        })}
                                        placeholder="#contact"
                                    />
                                </div>
                                
                                {/* 🎨 COLORS */}
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Background Color</span>
                                    <div className="nav-brand-colors">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.ctaButton.backgroundColor === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({
                                                    ctaButton: { ...navigationData.ctaButton, backgroundColor: brandColor }
                                                })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.ctaButton.backgroundColor === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Text Color</span>
                                    <div className="nav-brand-colors">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.ctaButton.textColor === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({
                                                    ctaButton: { ...navigationData.ctaButton, textColor: brandColor }
                                                })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.ctaButton.textColor === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* 📏 DIMENSIONS */}
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                    <span className="nav-color-label" style={{ marginBottom: '12px', display: 'block' }}>Dimensions</span>
                                    
                                    {/* Height Slider */}
                                    <div className="nav-slider-group">
                                        <span className="nav-slider-label">Height</span>
                                        <input
                                            type="range"
                                            min="30"
                                            max="80"
                                            value={isMobileView 
                                                ? (navigationData.ctaButton?.heightMobile ?? navigationData.ctaButton?.height ?? 44)
                                                : (navigationData.ctaButton?.height ?? 44)
                                            }
                                            onChange={(e) => {
                                                const key = isMobileView ? 'heightMobile' : 'height';
                                                onUpdate({
                                                    ctaButton: {
                                                        ...navigationData.ctaButton,
                                                        [key]: parseInt(e.target.value)
                                                    }
                                                });
                                            }}
                                            className="nav-slider"
                                        />
                                        <span className="nav-slider-value">
                                            {isMobileView 
                                                ? (navigationData.ctaButton?.heightMobile ?? navigationData.ctaButton?.height ?? 44)
                                                : (navigationData.ctaButton?.height ?? 44)
                                            }px
                                        </span>
                                    </div>
                                    
                                    {/* Border Radius Slider */}
                                    <div className="nav-slider-group">
                                        <span className="nav-slider-label">Border Radius</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            value={isMobileView 
                                                ? (navigationData.ctaButton?.borderRadiusMobile ?? navigationData.ctaButton?.borderRadius ?? 8)
                                                : (navigationData.ctaButton?.borderRadius ?? 8)
                                            }
                                            onChange={(e) => {
                                                const key = isMobileView ? 'borderRadiusMobile' : 'borderRadius';
                                                onUpdate({
                                                    ctaButton: {
                                                        ...navigationData.ctaButton,
                                                        [key]: parseInt(e.target.value)
                                                    }
                                                });
                                            }}
                                            className="nav-slider"
                                        />
                                        <span className="nav-slider-value">
                                            {isMobileView 
                                                ? (navigationData.ctaButton?.borderRadiusMobile ?? navigationData.ctaButton?.borderRadius ?? 8)
                                                : (navigationData.ctaButton?.borderRadius ?? 8)
                                            }px
                                        </span>
                                    </div>
                                    
                                    {/* Padding Horizontal Slider */}
                                    <div className="nav-slider-group">
                                        <span className="nav-slider-label">Padding (Horizontal)</span>
                                        <input
                                            type="range"
                                            min="8"
                                            max="60"
                                            value={navigationData.ctaButton?.paddingHorizontal ?? 24}
                                            onChange={(e) => {
                                                onUpdate({
                                                    ctaButton: {
                                                        ...navigationData.ctaButton,
                                                        paddingHorizontal: parseInt(e.target.value)
                                                    }
                                                });
                                            }}
                                            className="nav-slider"
                                        />
                                        <span className="nav-slider-value">
                                            {navigationData.ctaButton?.paddingHorizontal ?? 24}px
                                        </span>
                                    </div>
                                </div>
                                
                                {/* 🎨 BORDER */}
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                    <span className="nav-color-label" style={{ marginBottom: '12px', display: 'block' }}>Border</span>
                                    
                                    {/* Border Toggle */}
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.ctaButton?.borderEnabled || false}
                                                onChange={(e) => onUpdate({
                                                    ctaButton: {
                                                        ...navigationData.ctaButton,
                                                        borderEnabled: e.target.checked
                                                    }
                                                })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Enable Border</span>
                                        </label>
                                    </div>
                                    
                                    {navigationData.ctaButton?.borderEnabled && (
                                        <>
                                            {/* Border Width */}
                                            <div className="nav-slider-group">
                                                <span className="nav-slider-label">Border Width</span>
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="10"
                                                    value={navigationData.ctaButton?.borderWidth ?? 2}
                                                    onChange={(e) => {
                                                        onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                borderWidth: parseInt(e.target.value)
                                                            }
                                                        });
                                                    }}
                                                    className="nav-slider"
                                                />
                                                <span className="nav-slider-value">
                                                    {navigationData.ctaButton?.borderWidth ?? 2}px
                                                </span>
                                            </div>
                                            
                                            {/* Border Color */}
                                            <div className="nav-color-group">
                                                <span className="nav-color-label">Border Color</span>
                                                <div className="nav-brand-colors">
                                                    {Object.values(colors).map((brandColor, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            className={`nav-brand-color ${navigationData.ctaButton?.borderColor === brandColor ? 'selected' : ''}`}
                                                            style={{ backgroundColor: brandColor }}
                                                            onClick={() => onUpdate({
                                                                ctaButton: {
                                                                    ...navigationData.ctaButton,
                                                                    borderColor: brandColor
                                                                }
                                                            })}
                                                            title={`Color ${index + 1}`}
                                                        >
                                                            {navigationData.ctaButton?.borderColor === brandColor && (
                                                                <i className="fas fa-check"></i>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                
                                {/* ✨ SHADOW */}
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                    <span className="nav-color-label" style={{ marginBottom: '12px', display: 'block' }}>Shadow</span>
                                    
                                    {/* Shadow Toggle */}
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.ctaButton?.shadowEnabled || false}
                                                onChange={(e) => onUpdate({
                                                    ctaButton: {
                                                        ...navigationData.ctaButton,
                                                        shadowEnabled: e.target.checked
                                                    }
                                                })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Enable Shadow</span>
                                        </label>
                                    </div>
                                    
                                    {navigationData.ctaButton?.shadowEnabled && (
                                        <>
                                            {/* Shadow Intensity */}
                                            <div className="nav-slider-group">
                                                <span className="nav-slider-label">Intensity</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="30"
                                                    value={navigationData.ctaButton?.shadowIntensity ?? 10}
                                                    onChange={(e) => {
                                                        onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                shadowIntensity: parseInt(e.target.value)
                                                            }
                                                        });
                                                    }}
                                                    className="nav-slider"
                                                />
                                                <span className="nav-slider-value">
                                                    {navigationData.ctaButton?.shadowIntensity ?? 10}px
                                                </span>
                                            </div>
                                            
                                            {/* Shadow Distance */}
                                            <div className="nav-slider-group">
                                                <span className="nav-slider-label">Distance</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="20"
                                                    value={navigationData.ctaButton?.shadowDistance ?? 4}
                                                    onChange={(e) => {
                                                        onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                shadowDistance: parseInt(e.target.value)
                                                            }
                                                        });
                                                    }}
                                                    className="nav-slider"
                                                />
                                                <span className="nav-slider-value">
                                                    {navigationData.ctaButton?.shadowDistance ?? 4}px
                                                </span>
                                            </div>
                                            
                                            {/* Shadow Opacity */}
                                            <div className="nav-slider-group">
                                                <span className="nav-slider-label">Opacity</span>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="100"
                                                    value={navigationData.ctaButton?.shadowOpacity ?? 20}
                                                    onChange={(e) => {
                                                        onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                shadowOpacity: parseInt(e.target.value)
                                                            }
                                                        });
                                                    }}
                                                    className="nav-slider"
                                                />
                                                <span className="nav-slider-value">
                                                    {navigationData.ctaButton?.shadowOpacity ?? 20}%
                                                </span>
                                            </div>
                                            
                                            {/* Shadow Color */}
                                            <div className="nav-color-group">
                                                <span className="nav-color-label">Shadow Color</span>
                                                <div className="nav-brand-colors">
                                                    {Object.values(colors).map((brandColor, index) => (
                                                        <button
                                                            key={index}
                                                            type="button"
                                                            className={`nav-brand-color ${navigationData.ctaButton?.shadowColor === brandColor ? 'selected' : ''}`}
                                                            style={{ backgroundColor: brandColor }}
                                                            onClick={() => onUpdate({
                                                                ctaButton: {
                                                                    ...navigationData.ctaButton,
                                                                    shadowColor: brandColor
                                                                }
                                                            })}
                                                            title={`Color ${index + 1}`}
                                                        >
                                                            {navigationData.ctaButton?.shadowColor === brandColor && (
                                                                <i className="fas fa-check"></i>
                                                            )}
                                                        </button>
                                                    ))}
                                                    {/* Black as default shadow color */}
                                                    <button
                                                        type="button"
                                                        className={`nav-brand-color ${!navigationData.ctaButton?.shadowColor || navigationData.ctaButton?.shadowColor === '#000000' ? 'selected' : ''}`}
                                                        style={{ backgroundColor: '#000000' }}
                                                        onClick={() => onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                shadowColor: '#000000'
                                                            }
                                                        })}
                                                        title="Black"
                                                    >
                                                        {(!navigationData.ctaButton?.shadowColor || navigationData.ctaButton?.shadowColor === '#000000') && (
                                                            <i className="fas fa-check"></i>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                
                                {/* 🎯 ARROW */}
                                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                    <span className="nav-color-label" style={{ marginBottom: '12px', display: 'block' }}>Arrow Icon</span>
                                    
                                    {/* Arrow Toggle */}
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.ctaButton?.arrowEnabled || false}
                                                onChange={(e) => onUpdate({
                                                    ctaButton: {
                                                        ...navigationData.ctaButton,
                                                        arrowEnabled: e.target.checked
                                                    }
                                                })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Enable Arrow</span>
                                        </label>
                                    </div>
                                    
                                    {navigationData.ctaButton?.arrowEnabled && (
                                        <>
                                            {/* Arrow Icon Selection - Grid */}
                                            <div style={{ marginBottom: '12px' }}>
                                                <span className="nav-slider-label" style={{ display: 'block', marginBottom: '8px' }}>Icon Style</span>
                                                <div style={{ 
                                                    display: 'grid', 
                                                    gridTemplateColumns: 'repeat(3, 1fr)', 
                                                    gap: '6px' 
                                                }}>
                                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((iconNum) => (
                                                        <button
                                                            key={iconNum}
                                                            type="button"
                                                            onClick={() => onUpdate({
                                                                ctaButton: {
                                                                    ...navigationData.ctaButton,
                                                                    arrowIcon: `arrow_${iconNum}`
                                                                }
                                                            })}
                                                            style={{
                                                                padding: '8px',
                                                                background: (navigationData.ctaButton?.arrowIcon || 'arrow_1') === `arrow_${iconNum}` 
                                                                    ? 'rgba(255, 255, 255, 0.2)' 
                                                                    : 'rgba(255, 255, 255, 0.05)',
                                                                border: (navigationData.ctaButton?.arrowIcon || 'arrow_1') === `arrow_${iconNum}`
                                                                    ? '2px solid rgba(255, 255, 255, 0.4)'
                                                                    : '1px solid rgba(255, 255, 255, 0.1)',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if ((navigationData.ctaButton?.arrowIcon || 'arrow_1') !== `arrow_${iconNum}`) {
                                                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if ((navigationData.ctaButton?.arrowIcon || 'arrow_1') !== `arrow_${iconNum}`) {
                                                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                                                }
                                                            }}
                                                        >
                                                            <img 
                                                                src={`/img/arrow_${iconNum}.svg`} 
                                                                alt={`Arrow ${iconNum}`}
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    filter: 'invert(1)'
                                                                }}
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            {/* Arrow Size */}
                                            <div className="nav-slider-group">
                                                <span className="nav-slider-label">Size</span>
                                                <input
                                                    type="range"
                                                    min="12"
                                                    max="48"
                                                    value={navigationData.ctaButton?.arrowSize ?? 24}
                                                    onChange={(e) => {
                                                        onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                arrowSize: parseInt(e.target.value)
                                                            }
                                                        });
                                                    }}
                                                    className="nav-slider"
                                                />
                                                <span className="nav-slider-value">
                                                    {navigationData.ctaButton?.arrowSize ?? 24}px
                                                </span>
                                            </div>
                                            
                                            {/* Arrow Invert Toggle */}
                                            <div className="nav-toggle-group">
                                                <label className="nav-toggle-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={navigationData.ctaButton?.arrowInvert || false}
                                                        onChange={(e) => onUpdate({
                                                            ctaButton: {
                                                                ...navigationData.ctaButton,
                                                                arrowInvert: e.target.checked
                                                            }
                                                        })}
                                                        className="nav-toggle-checkbox"
                                                    />
                                                    <span className="nav-toggle-text">Invert Color</span>
                                                </label>
                                            </div>
                                            
                                            {/* Arrow Direction */}
                                            <div style={{ marginTop: '12px' }}>
                                                <span className="nav-slider-label" style={{ display: 'block', marginBottom: '8px' }}>Direction</span>
                                                <div style={{ 
                                                    display: 'grid', 
                                                    gridTemplateColumns: 'repeat(2, 1fr)', 
                                                    gap: '6px' 
                                                }}>
                                                    {['left', 'right', 'up', 'down'].map((dir) => (
                                                        <button
                                                            key={dir}
                                                            type="button"
                                                            onClick={() => onUpdate({
                                                                ctaButton: {
                                                                    ...navigationData.ctaButton,
                                                                    arrowDirection: dir
                                                                }
                                                            })}
                                                            style={{
                                                                padding: '10px',
                                                                background: (navigationData.ctaButton?.arrowDirection || 'right') === dir 
                                                                    ? 'rgba(255, 255, 255, 0.2)' 
                                                                    : 'rgba(255, 255, 255, 0.05)',
                                                                border: (navigationData.ctaButton?.arrowDirection || 'right') === dir
                                                                    ? '2px solid rgba(255, 255, 255, 0.4)'
                                                                    : '1px solid rgba(255, 255, 255, 0.1)',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                color: 'white',
                                                                fontSize: '14px',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if ((navigationData.ctaButton?.arrowDirection || 'right') !== dir) {
                                                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if ((navigationData.ctaButton?.arrowDirection || 'right') !== dir) {
                                                                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                                                }
                                                            }}
                                                        >
                                                            <i className={`fas fa-arrow-${dir}`}></i>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                        
                        <span className="nav-section-label">CTA Button</span>
                    </div>
                )}
                
                {/* 5️⃣ STYLING SECTION */}
                {expandedSection === 'styling' && (
                    <div className="nav-expanded-section">
                        {/* 🎯 SYMMETRIC LAYOUT TOGGLE */}
                        <div className="nav-toggle-group" style={{ 
                            marginBottom: '16px', 
                            paddingBottom: '16px', 
                            borderBottom: '1px solid rgba(255, 255, 255, 0.15)' 
                        }}>
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={navigationData.symmetricLayout !== false}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        console.log('Symmetric Layout toggle:', newValue);
                                        onUpdate({ symmetricLayout: newValue });
                                    }}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">
                                    Symmetric Layout
                                    <span style={{ 
                                        display: 'block', 
                                        fontSize: '9px', 
                                        color: 'rgba(255, 255, 255, 0.6)', 
                                        marginTop: '4px',
                                        fontWeight: '400'
                                    }}>
                                        Linke & rechte Sektion bekommen gleich viel Platz
                                    </span>
                                </span>
                            </label>
                        </div>
                        
                        {/* Padding Left */}
                        <div className="nav-slider-group">
                            <span className="nav-slider-label">Pad Left</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={isMobileView ? navigationData.paddingLeftMobile : navigationData.paddingLeft}
                                onChange={(e) => {
                                    const key = isMobileView ? 'paddingLeftMobile' : 'paddingLeft';
                                    onUpdate({ [key]: parseInt(e.target.value) });
                                }}
                                className="nav-slider"
                            />
                            <span className="nav-slider-value">{isMobileView ? navigationData.paddingLeftMobile : navigationData.paddingLeft}px</span>
                        </div>
                        
                        {/* Padding Right */}
                        <div className="nav-slider-group">
                            <span className="nav-slider-label">Pad Right</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={isMobileView ? navigationData.paddingRightMobile : navigationData.paddingRight}
                                onChange={(e) => {
                                    const key = isMobileView ? 'paddingRightMobile' : 'paddingRight';
                                    onUpdate({ [key]: parseInt(e.target.value) });
                                }}
                                className="nav-slider"
                            />
                            <span className="nav-slider-value">{isMobileView ? navigationData.paddingRightMobile : navigationData.paddingRight}px</span>
                        </div>
                        
                        {/* Background Color */}
                        <div className="nav-color-group">
                            <span className="nav-color-label">Background Color</span>
                            <div className="nav-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`nav-brand-color ${navigationData.backgroundColor === brandColor ? 'selected' : ''}`}
                                        style={{ backgroundColor: brandColor }}
                                        onClick={() => onUpdate({ backgroundColor: brandColor })}
                                        title={`Color ${index + 1}`}
                                    >
                                        {navigationData.backgroundColor === brandColor && (
                                            <i className="fas fa-check"></i>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Text Color */}
                        <div className="nav-color-group">
                            <span className="nav-color-label">Text Color</span>
                            <div className="nav-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`nav-brand-color ${navigationData.textColor === brandColor ? 'selected' : ''}`}
                                        style={{ backgroundColor: brandColor }}
                                        onClick={() => onUpdate({ textColor: brandColor })}
                                        title={`Color ${index + 1}`}
                                    >
                                        {navigationData.textColor === brandColor && (
                                            <i className="fas fa-check"></i>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Text Size (für Navigation Links) */}
                        <div className="nav-slider-group">
                            <label className="nav-slider-label">
                                Text Size ({isMobileView ? 'Mobile' : 'Desktop'})
                                <span className="nav-slider-value">
                                    {isMobileView ? navigationData.textSizeMobile || 14 : navigationData.textSize || 16}px
                                </span>
                            </label>
                            <input
                                type="range"
                                min="10"
                                max="40"
                                value={isMobileView ? navigationData.textSizeMobile || 14 : navigationData.textSize || 16}
                                onChange={(e) => {
                                    const key = isMobileView ? 'textSizeMobile' : 'textSize';
                                    onUpdate({ [key]: parseInt(e.target.value) });
                                }}
                                className="nav-slider"
                            />
                        </div>
                        
                        {/* Button Text Size (für CTA Button) */}
                        {navigationData.ctaButton?.enabled && (
                            <div className="nav-slider-group">
                                <label className="nav-slider-label">
                                    Button Text Size ({isMobileView ? 'Mobile' : 'Desktop'})
                                    <span className="nav-slider-value">
                                        {isMobileView ? navigationData.ctaButton?.textSizeMobile || 14 : navigationData.ctaButton?.textSize || 15}px
                                    </span>
                                </label>
                                <input
                                    type="range"
                                    min="10"
                                    max="40"
                                    value={isMobileView ? navigationData.ctaButton?.textSizeMobile || 14 : navigationData.ctaButton?.textSize || 15}
                                    onChange={(e) => {
                                        const key = isMobileView ? 'textSizeMobile' : 'textSize';
                                        onUpdate({
                                            ctaButton: {
                                                ...navigationData.ctaButton,
                                                [key]: parseInt(e.target.value)
                                            }
                                        });
                                    }}
                                    className="nav-slider"
                                />
                            </div>
                        )}
                        
                        {/* Height */}
                        <div className="nav-slider-group">
                            <label className="nav-slider-label">
                                Height ({isMobileView ? 'Mobile' : 'Desktop'})
                                <span className="nav-slider-value">
                                    {isMobileView ? navigationData.heightMobile : navigationData.height}px
                                </span>
                            </label>
                            <input
                                type="range"
                                min="50"
                                max="120"
                                value={isMobileView ? navigationData.heightMobile : navigationData.height}
                                onChange={(e) => {
                                    const key = isMobileView ? 'heightMobile' : 'height';
                                    onUpdate({ [key]: parseInt(e.target.value) });
                                }}
                                className="nav-slider"
                            />
                        </div>
                        
                        {/* Sticky */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={!!navigationData.sticky}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        console.log('Sticky toggle - Current:', navigationData.sticky, '→ New:', newValue);
                                        onUpdate({ sticky: newValue });
                                    }}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Sticky (Fixed after Page Start)</span>
                            </label>
                        </div>
                        
                        {/* Hide on Scroll Down - Nur wenn Sticky aktiv */}
                        {navigationData.sticky && (
                            <div className="nav-toggle-group">
                                <label className="nav-toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={!!navigationData.hideOnScrollDown}
                                        onChange={(e) => {
                                            onUpdate({ hideOnScrollDown: e.target.checked });
                                        }}
                                        className="nav-toggle-checkbox"
                                    />
                                    <span className="nav-toggle-text">Hide on Scroll Down</span>
                                </label>
                            </div>
                        )}
                        
                        {/* Shadow */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={!!navigationData.shadow}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        console.log('Shadow toggle - Current:', navigationData.shadow, '→ New:', newValue);
                                        onUpdate({ shadow: newValue });
                                    }}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Shadow</span>
                            </label>
                        </div>
                        
                        {/* Border Bottom */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={!!navigationData.borderBottomEnabled}
                                    onChange={(e) => {
                                        onUpdate({ borderBottomEnabled: e.target.checked });
                                    }}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Border Bottom</span>
                            </label>
                        </div>
                        
                        {/* Border Bottom Settings */}
                        {navigationData.borderBottomEnabled && (
                            <>
                                <div className="nav-slider-group">
                                    <span className="nav-slider-label">Width</span>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={navigationData.borderBottomWidth || 1}
                                        onChange={(e) => {
                                            onUpdate({ borderBottomWidth: parseInt(e.target.value) });
                                        }}
                                        className="nav-slider"
                                    />
                                    <span className="nav-slider-value">{navigationData.borderBottomWidth || 1}px</span>
                                </div>
                                
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Border Color</span>
                                    <div className="nav-brand-colors">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.borderBottomColor === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({ borderBottomColor: brandColor })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.borderBottomColor === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* Backdrop Filter */}
                        <div className="nav-toggle-group">
                            <label className="nav-toggle-label">
                                <input
                                    type="checkbox"
                                    checked={!!navigationData.backdropFilterEnabled}
                                    onChange={(e) => {
                                        onUpdate({ backdropFilterEnabled: e.target.checked });
                                    }}
                                    className="nav-toggle-checkbox"
                                />
                                <span className="nav-toggle-text">Backdrop Filter (Glassmorphism)</span>
                            </label>
                        </div>
                        
                        {/* Backdrop Filter Blur Amount */}
                        {navigationData.backdropFilterEnabled && (
                            <>
                                <div className="nav-slider-group">
                                    <span className="nav-slider-label">Blur</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="30"
                                        value={navigationData.backdropFilterBlur || 10}
                                        onChange={(e) => {
                                            onUpdate({ backdropFilterBlur: parseInt(e.target.value) });
                                        }}
                                        className="nav-slider"
                                    />
                                    <span className="nav-slider-value">{navigationData.backdropFilterBlur || 10}px</span>
                                </div>
                                
                                {/* Background Opacity */}
                                <div className="nav-slider-group">
                                    <span className="nav-slider-label">Opacity</span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={navigationData.backgroundOpacity || 100}
                                        onChange={(e) => {
                                            onUpdate({ backgroundOpacity: parseInt(e.target.value) });
                                        }}
                                        className="nav-slider"
                                    />
                                    <span className="nav-slider-value">{navigationData.backgroundOpacity || 100}%</span>
                                </div>
                            </>
                        )}
                        
                        <span className="nav-section-label">Styling</span>
                    </div>
                )}
                
                {/* 6️⃣ TEXT FONT SECTION - Für Navigation Links */}
                {expandedSection === 'textFont' && (
                    <div className="nav-expanded-section">
                        <div className="nav-font-family-list">
                            {/* ⭐ BRAND FONTS - Oben mit Star */}
                            {spaceFonts?.globalBigFont && (
                                <button
                                    key="brand-big-font"
                                    type="button"
                                    className={`nav-font-family-option brand-font ${navigationData.textFontFamily === spaceFonts.globalBigFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: spaceFonts.globalBigFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({ textFontFamily: spaceFonts.globalBigFont });
                                    }}
                                    title={`${spaceFonts.globalBigFont} (Brand Headline Font)`}
                                >
                                    ⭐ {spaceFonts.globalBigFont}
                                </button>
                            )}
                            
                            {spaceFonts?.globalSmallFont && spaceFonts.globalSmallFont !== spaceFonts.globalBigFont && (
                                <button
                                    key="brand-small-font"
                                    type="button"
                                    className={`nav-font-family-option brand-font ${navigationData.textFontFamily === spaceFonts.globalSmallFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: spaceFonts.globalSmallFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({ textFontFamily: spaceFonts.globalSmallFont });
                                    }}
                                    title={`${spaceFonts.globalSmallFont} (Brand Body Font)`}
                                >
                                    ⭐ {spaceFonts.globalSmallFont}
                                </button>
                            )}
                            
                            {/* Divider nach Brand Fonts */}
                            {(spaceFonts?.globalBigFont || spaceFonts?.globalSmallFont) && (
                                <div className="nav-font-divider"></div>
                            )}
                            
                            {/* Custom Fonts - gefiltert (ohne Brand Fonts) */}
                            {fonts.customFonts && fonts.customFonts.length > 0 && (
                                <>
                                    {fonts.customFonts
                                        .filter(customFont => 
                                            customFont.name !== spaceFonts?.globalBigFont && 
                                            customFont.name !== spaceFonts?.globalSmallFont
                                        )
                                        .map((customFont, index) => (
                                        <button
                                            key={`custom-${index}`}
                                            type="button"
                                            className={`nav-font-family-option ${navigationData.textFontFamily === customFont.name ? 'is-active' : ''}`}
                                            style={{ fontFamily: customFont.name }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onUpdate({ textFontFamily: customFont.name });
                                            }}
                                            title={customFont.name}
                                        >
                                            {customFont.name}
                                        </button>
                                    ))}
                                    {fonts.customFonts.filter(customFont => 
                                        customFont.name !== spaceFonts?.globalBigFont && 
                                        customFont.name !== spaceFonts?.globalSmallFont
                                    ).length > 0 && <div className="nav-font-divider"></div>}
                                </>
                            )}

                            {/* Title Fonts - gefiltert (ohne Brand Fonts) */}
                            {titleFonts
                                .filter(font => 
                                    font !== spaceFonts?.globalBigFont && 
                                    font !== spaceFonts?.globalSmallFont
                                )
                                .map((font, index) => (
                                <button
                                    key={`title-${index}`}
                                    type="button"
                                    className={`nav-font-family-option ${navigationData.textFontFamily === font ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({ textFontFamily: font });
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                            ))}

                            {/* Text Fonts - gefiltert (ohne Brand Fonts) */}
                            {textFonts
                                .filter(font => 
                                    font !== spaceFonts?.globalBigFont && 
                                    font !== spaceFonts?.globalSmallFont
                                )
                                .map((font, index) => (
                                <button
                                    key={`text-${index}`}
                                    type="button"
                                    className={`nav-font-family-option ${navigationData.textFontFamily === font ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({ textFontFamily: font });
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                            ))}
                        </div>
                        <span className="nav-section-label">Text Font</span>
                    </div>
                )}
                
                {/* 7️⃣ CTA FONT SECTION - Für CTA Button */}
                {expandedSection === 'ctaFont' && (
                    <div className="nav-expanded-section">
                        <div className="nav-font-family-list">
                            {/* ⭐ BRAND FONTS - Oben mit Star */}
                            {spaceFonts?.globalBigFont && (
                                <button
                                    key="brand-big-font"
                                    type="button"
                                    className={`nav-font-family-option brand-font ${navigationData.ctaButton?.fontFamily === spaceFonts.globalBigFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: spaceFonts.globalBigFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({
                                            ctaButton: { ...navigationData.ctaButton, fontFamily: spaceFonts.globalBigFont }
                                        });
                                    }}
                                    title={`${spaceFonts.globalBigFont} (Brand Headline Font)`}
                                >
                                    ⭐ {spaceFonts.globalBigFont}
                                </button>
                            )}
                            
                            {spaceFonts?.globalSmallFont && spaceFonts.globalSmallFont !== spaceFonts.globalBigFont && (
                                <button
                                    key="brand-small-font"
                                    type="button"
                                    className={`nav-font-family-option brand-font ${navigationData.ctaButton?.fontFamily === spaceFonts.globalSmallFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: spaceFonts.globalSmallFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({
                                            ctaButton: { ...navigationData.ctaButton, fontFamily: spaceFonts.globalSmallFont }
                                        });
                                    }}
                                    title={`${spaceFonts.globalSmallFont} (Brand Body Font)`}
                                >
                                    ⭐ {spaceFonts.globalSmallFont}
                                </button>
                            )}
                            
                            {/* Divider nach Brand Fonts */}
                            {(spaceFonts?.globalBigFont || spaceFonts?.globalSmallFont) && (
                                <div className="nav-font-divider"></div>
                            )}
                            
                            {/* Custom Fonts - gefiltert (ohne Brand Fonts) */}
                            {fonts.customFonts && fonts.customFonts.length > 0 && (
                                <>
                                    {fonts.customFonts
                                        .filter(customFont => 
                                            customFont.name !== spaceFonts?.globalBigFont && 
                                            customFont.name !== spaceFonts?.globalSmallFont
                                        )
                                        .map((customFont, index) => (
                                        <button
                                            key={`custom-${index}`}
                                            type="button"
                                            className={`nav-font-family-option ${navigationData.ctaButton?.fontFamily === customFont.name ? 'is-active' : ''}`}
                                            style={{ fontFamily: customFont.name }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onUpdate({
                                                    ctaButton: { ...navigationData.ctaButton, fontFamily: customFont.name }
                                                });
                                            }}
                                            title={customFont.name}
                                        >
                                            {customFont.name}
                                        </button>
                                    ))}
                                    {fonts.customFonts.filter(customFont => 
                                        customFont.name !== spaceFonts?.globalBigFont && 
                                        customFont.name !== spaceFonts?.globalSmallFont
                                    ).length > 0 && <div className="nav-font-divider"></div>}
                                </>
                            )}

                            {/* Title Fonts - gefiltert (ohne Brand Fonts) */}
                            {titleFonts
                                .filter(font => 
                                    font !== spaceFonts?.globalBigFont && 
                                    font !== spaceFonts?.globalSmallFont
                                )
                                .map((font, index) => (
                                <button
                                    key={`title-${index}`}
                                    type="button"
                                    className={`nav-font-family-option ${navigationData.ctaButton?.fontFamily === font ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({
                                            ctaButton: { ...navigationData.ctaButton, fontFamily: font }
                                        });
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                            ))}

                            {/* Text Fonts - gefiltert (ohne Brand Fonts) */}
                            {textFonts
                                .filter(font => 
                                    font !== spaceFonts?.globalBigFont && 
                                    font !== spaceFonts?.globalSmallFont
                                )
                                .map((font, index) => (
                                <button
                                    key={`text-${index}`}
                                    type="button"
                                    className={`nav-font-family-option ${navigationData.ctaButton?.fontFamily === font ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onUpdate({
                                            ctaButton: { ...navigationData.ctaButton, fontFamily: font }
                                        });
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                            ))}
                        </div>
                        <span className="nav-section-label">CTA Button Font</span>
                    </div>
                )}
                
                {/* 8️⃣ MOBILE SETTINGS SECTION */}
                {expandedSection === 'mobile' && (
                    <div className="nav-expanded-section">
                        {/* Desktop/Mobile Toggle */}
                        <div className="nav-device-toggle-row">
                            <span className="nav-device-toggle-label">View</span>
                            <div 
                                className={`nav-device-toggle ${mobileDevice === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setMobileDevice(mobileDevice === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="nav-device-toggle-slider"></div>
                            </div>
                            <span className="nav-device-toggle-label">{mobileDevice === 'desktop' ? 'Desktop' : 'Mobile'}</span>
                        </div>
                        
                        {/* Show current device info */}
                        <div className="nav-mobile-info">
                            <i className={`fas fa-${mobileDevice === 'desktop' ? 'desktop' : 'mobile-alt'}`}></i>
                            <span>Configure {mobileDevice === 'desktop' ? 'Desktop' : 'Mobile'} visibility</span>
                        </div>
                        
                        {/* Logo Visibility on Mobile */}
                        {mobileDevice === 'mobile' && (
                            <>
                                <div className="nav-toggle-group">
                                    <label className="nav-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={navigationData.showLogoMobile !== false}
                                            onChange={(e) => onUpdate({ showLogoMobile: e.target.checked })}
                                            className="nav-toggle-checkbox"
                                        />
                                        <span className="nav-toggle-text">Show Logo on Mobile</span>
                                    </label>
                                </div>
                                
                                {/* Mobile Menu Toggle */}
                                <div className="nav-toggle-group">
                                    <label className="nav-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={navigationData.useMobileMenu !== false}
                                            onChange={(e) => onUpdate({ useMobileMenu: e.target.checked })}
                                            className="nav-toggle-checkbox"
                                        />
                                        <span className="nav-toggle-text">Use Mobile Menu (Hamburger)</span>
                                    </label>
                                </div>
                                
                                <div className="nav-mobile-hint">
                                    <i className="fas fa-info-circle"></i>
                                    <span>Links are shown in hamburger menu when enabled</span>
                                </div>
                                
                                {/* Mobile Menu Width */}
                                {navigationData.useMobileMenu && (
                                    <>
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Menu Width
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuWidth || 80}%
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="50"
                                                max="100"
                                                step="5"
                                                value={navigationData.mobileMenuWidth || 80}
                                                onChange={(e) => onUpdate({ mobileMenuWidth: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                        
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Hamburger Button Size
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuButtonSize || 44}px
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="30"
                                                max="60"
                                                value={navigationData.mobileMenuButtonSize || 44}
                                                onChange={(e) => onUpdate({ mobileMenuButtonSize: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                    </>
                                )}
                                
                                {/* Logo Position on Mobile */}
                                <div className="nav-position-group">
                                    <span className="nav-color-label">Logo Position (Mobile)</span>
                                    <div className="nav-position-buttons">
                                        <button
                                            className={`nav-position-btn ${(navigationData.logoPositionMobile || navigationData.logoPosition) === 'left' ? 'active' : ''}`}
                                            onClick={() => onUpdate({ logoPositionMobile: 'left' })}
                                            title="Left"
                                        >
                                            <i className="fas fa-align-left"></i>
                                            <span>Left</span>
                                        </button>
                                        <button
                                            className={`nav-position-btn ${(navigationData.logoPositionMobile || navigationData.logoPosition) === 'center' ? 'active' : ''}`}
                                            onClick={() => onUpdate({ logoPositionMobile: 'center' })}
                                            title="Center"
                                        >
                                            <i className="fas fa-align-center"></i>
                                            <span>Center</span>
                                        </button>
                                        <button
                                            className={`nav-position-btn ${(navigationData.logoPositionMobile || navigationData.logoPosition) === 'right' ? 'active' : ''}`}
                                            onClick={() => onUpdate({ logoPositionMobile: 'right' })}
                                            title="Right"
                                        >
                                            <i className="fas fa-align-right"></i>
                                            <span>Right</span>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Social Icons Position on Mobile (wenn NICHT im Hamburger Menu) */}
                                {!navigationData.useMobileMenu && navigationData.socialIconsVisible && navigationData.showSocialIconsMobile && (
                                    <div className="nav-position-group">
                                        <span className="nav-color-label">Social Position (Mobile)</span>
                                        <div className="nav-position-buttons">
                                            <button
                                                className={`nav-position-btn ${(navigationData.socialIconsPositionMobile || navigationData.socialIconsPosition) === 'left' ? 'active' : ''}`}
                                                onClick={() => onUpdate({ socialIconsPositionMobile: 'left' })}
                                                title="Left"
                                            >
                                                <i className="fas fa-align-left"></i>
                                                <span>Left</span>
                                            </button>
                                            <button
                                                className={`nav-position-btn ${(navigationData.socialIconsPositionMobile || navigationData.socialIconsPosition) === 'center' ? 'active' : ''}`}
                                                onClick={() => onUpdate({ socialIconsPositionMobile: 'center' })}
                                                title="Center"
                                            >
                                                <i className="fas fa-align-center"></i>
                                                <span>Center</span>
                                            </button>
                                            <button
                                                className={`nav-position-btn ${(navigationData.socialIconsPositionMobile || navigationData.socialIconsPosition) === 'right' ? 'active' : ''}`}
                                                onClick={() => onUpdate({ socialIconsPositionMobile: 'right' })}
                                                title="Right"
                                            >
                                                <i className="fas fa-align-right"></i>
                                                <span>Right</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                {/* CTA Button Position on Mobile (wenn NICHT im Hamburger Menu) */}
                                {!navigationData.useMobileMenu && navigationData.ctaButton?.enabled && navigationData.showCTAButtonMobile && (
                                    <div className="nav-position-group">
                                        <span className="nav-color-label">CTA Position (Mobile)</span>
                                        <div className="nav-position-buttons">
                                            <button
                                                className={`nav-position-btn ${(navigationData.ctaButtonPositionMobile || navigationData.ctaButtonPosition) === 'left' ? 'active' : ''}`}
                                                onClick={() => onUpdate({ ctaButtonPositionMobile: 'left' })}
                                                title="Left"
                                            >
                                                <i className="fas fa-align-left"></i>
                                                <span>Left</span>
                                            </button>
                                            <button
                                                className={`nav-position-btn ${(navigationData.ctaButtonPositionMobile || navigationData.ctaButtonPosition) === 'center' ? 'active' : ''}`}
                                                onClick={() => onUpdate({ ctaButtonPositionMobile: 'center' })}
                                                title="Center"
                                            >
                                                <i className="fas fa-align-center"></i>
                                                <span>Center</span>
                                            </button>
                                            <button
                                                className={`nav-position-btn ${(navigationData.ctaButtonPositionMobile || navigationData.ctaButtonPosition) === 'right' ? 'active' : ''}`}
                                                onClick={() => onUpdate({ ctaButtonPositionMobile: 'right' })}
                                                title="Right"
                                            >
                                                <i className="fas fa-align-right"></i>
                                                <span>Right</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                                
                                {/* Social Icons Visibility on Mobile */}
                                {navigationData.socialIconsVisible && (
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.showSocialIconsMobile !== false}
                                                onChange={(e) => onUpdate({ showSocialIconsMobile: e.target.checked })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Show Social Icons on Mobile</span>
                                        </label>
                                    </div>
                                )}
                                
                                {/* CTA Button Visibility on Mobile */}
                                {navigationData.ctaButton?.enabled && (
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.showCTAButtonMobile !== false}
                                                onChange={(e) => onUpdate({ showCTAButtonMobile: e.target.checked })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Show CTA Button on Mobile</span>
                                        </label>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {/* Desktop Info */}
                        {mobileDevice === 'desktop' && (
                            <div className="nav-mobile-info">
                                <i className="fas fa-lightbulb"></i>
                                <span>All elements are visible on desktop. Switch to Mobile to configure mobile visibility.</span>
                            </div>
                        )}
                        
                        {/* Mobile Menu Content (nur bei Mobile) */}
                        {mobileDevice === 'mobile' && navigationData.useMobileMenu && (
                            <>
                                <div className="nav-section-divider"></div>
                                <div className="nav-section-title">Mobile Menu Content</div>
                                
                                {/* Show Logo in Menu */}
                                {navigationData.logo && (
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={!!navigationData.mobileMenuShowLogo}
                                                onChange={(e) => onUpdate({ mobileMenuShowLogo: e.target.checked })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Show Logo in Menu</span>
                                        </label>
                                    </div>
                                )}
                                
                                {/* Logo Dimensions in Menu */}
                                {navigationData.logo && navigationData.mobileMenuShowLogo && (
                                    <>
                                        {/* Logo Alignment */}
                                        <div className="nav-position-group">
                                            <span className="nav-color-label">Logo Alignment (Menu)</span>
                                            <div className="nav-position-buttons">
                                                <button
                                                    className={`nav-position-btn ${(navigationData.mobileMenuLogoAlignment || 'center') === 'left' ? 'active' : ''}`}
                                                    onClick={() => onUpdate({ mobileMenuLogoAlignment: 'left' })}
                                                    title="Left"
                                                >
                                                    <i className="fas fa-align-left"></i>
                                                    <span>Left</span>
                                                </button>
                                                <button
                                                    className={`nav-position-btn ${(navigationData.mobileMenuLogoAlignment || 'center') === 'center' ? 'active' : ''}`}
                                                    onClick={() => onUpdate({ mobileMenuLogoAlignment: 'center' })}
                                                    title="Center"
                                                >
                                                    <i className="fas fa-align-center"></i>
                                                    <span>Center</span>
                                                </button>
                                                <button
                                                    className={`nav-position-btn ${(navigationData.mobileMenuLogoAlignment || 'center') === 'right' ? 'active' : ''}`}
                                                    onClick={() => onUpdate({ mobileMenuLogoAlignment: 'right' })}
                                                    title="Right"
                                                >
                                                    <i className="fas fa-align-right"></i>
                                                    <span>Right</span>
                                                </button>
                                            </div>
                                        </div>
                                        
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Logo Width (Menu)
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuLogoWidth || 100}px
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="50"
                                                max="200"
                                                value={navigationData.mobileMenuLogoWidth || 100}
                                                onChange={(e) => onUpdate({ mobileMenuLogoWidth: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                        
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Logo Height (Menu)
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuLogoHeight || 40}px
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="20"
                                                max="100"
                                                value={navigationData.mobileMenuLogoHeight || 40}
                                                onChange={(e) => onUpdate({ mobileMenuLogoHeight: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                        
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Logo Margin Top
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuLogoMarginTop || 20}px
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="60"
                                                value={navigationData.mobileMenuLogoMarginTop || 20}
                                                onChange={(e) => onUpdate({ mobileMenuLogoMarginTop: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                        
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Logo Margin Bottom
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuLogoMarginBottom || 20}px
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="60"
                                                value={navigationData.mobileMenuLogoMarginBottom || 20}
                                                onChange={(e) => onUpdate({ mobileMenuLogoMarginBottom: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                    </>
                                )}
                                
                                {/* Show Links in Menu */}
                                <div className="nav-toggle-group">
                                    <label className="nav-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={navigationData.mobileMenuShowLinks !== false}
                                            onChange={(e) => onUpdate({ mobileMenuShowLinks: e.target.checked })}
                                            className="nav-toggle-checkbox"
                                        />
                                        <span className="nav-toggle-text">Show Links in Menu</span>
                                    </label>
                                </div>
                                
                                {/* Show Social Icons in Menu */}
                                {navigationData.socialIconsVisible && navigationData.showSocialIconsMobile && (
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.mobileMenuShowSocial !== false}
                                                onChange={(e) => onUpdate({ mobileMenuShowSocial: e.target.checked })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Show Social Icons in Menu</span>
                                        </label>
                                    </div>
                                )}
                                
                                {/* Show CTA Button in Menu - Getrennt von showCTAButtonMobile! */}
                                {navigationData.ctaButton?.enabled && (
                                    <div className="nav-toggle-group">
                                        <label className="nav-toggle-label">
                                            <input
                                                type="checkbox"
                                                checked={navigationData.mobileMenuShowCTA !== false}
                                                onChange={(e) => onUpdate({ mobileMenuShowCTA: e.target.checked })}
                                                className="nav-toggle-checkbox"
                                            />
                                            <span className="nav-toggle-text">Show CTA Button in Menu</span>
                                        </label>
                                    </div>
                                )}
                                
                                <div className="nav-mobile-hint">
                                    <i className="fas fa-lightbulb"></i>
                                    <span>CTA can be shown in nav bar AND/OR in mobile menu separately</span>
                                </div>
                                
                                <div className="nav-section-divider"></div>
                                <div className="nav-section-title">Mobile Menu Styling</div>
                                
                                {/* Mobile Menu Background Color */}
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Menu Background</span>
                                    <div className="nav-brand-colors">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.mobileMenuBackgroundColor === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({ mobileMenuBackgroundColor: brandColor })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.mobileMenuBackgroundColor === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Mobile Menu Text Color */}
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Menu Text Color</span>
                                    <div className="nav-brand-colors">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.mobileMenuTextColor === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({ mobileMenuTextColor: brandColor })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.mobileMenuTextColor === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Mobile Menu Text Size */}
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Menu Text Size
                                        <span className="nav-slider-value">
                                            {navigationData.mobileMenuTextSize || 16}px
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="12"
                                        max="40"
                                        value={navigationData.mobileMenuTextSize || 16}
                                        onChange={(e) => onUpdate({ mobileMenuTextSize: parseInt(e.target.value) })}
                                        className="nav-slider"
                                    />
                                </div>
                                
                                {/* Mobile Menu Border */}
                                <div className="nav-toggle-group">
                                    <label className="nav-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={!!navigationData.mobileMenuBorderEnabled}
                                            onChange={(e) => onUpdate({ mobileMenuBorderEnabled: e.target.checked })}
                                            className="nav-toggle-checkbox"
                                        />
                                        <span className="nav-toggle-text">Menu Border</span>
                                    </label>
                                </div>
                                
                                {navigationData.mobileMenuBorderEnabled && (
                                    <>
                                        <div className="nav-slider-group">
                                            <span className="nav-slider-label">Border Width</span>
                                            <input
                                                type="range"
                                                min="1"
                                                max="5"
                                                value={navigationData.mobileMenuBorderWidth || 1}
                                                onChange={(e) => onUpdate({ mobileMenuBorderWidth: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                            <span className="nav-slider-value">{navigationData.mobileMenuBorderWidth || 1}px</span>
                                        </div>
                                        
                                        <div className="nav-color-group">
                                            <span className="nav-color-label">Border Color</span>
                                            <div className="nav-brand-colors">
                                                {Object.values(colors).map((brandColor, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        className={`nav-brand-color ${navigationData.mobileMenuBorderColor === brandColor ? 'selected' : ''}`}
                                                        style={{ backgroundColor: brandColor }}
                                                        onClick={() => onUpdate({ mobileMenuBorderColor: brandColor })}
                                                        title={`Color ${index + 1}`}
                                                    >
                                                        {navigationData.mobileMenuBorderColor === brandColor && (
                                                            <i className="fas fa-check"></i>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                                
                                {/* Mobile Menu Shadow */}
                                <div className="nav-toggle-group">
                                    <label className="nav-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={!!navigationData.mobileMenuShadowEnabled}
                                            onChange={(e) => onUpdate({ mobileMenuShadowEnabled: e.target.checked })}
                                            className="nav-toggle-checkbox"
                                        />
                                        <span className="nav-toggle-text">Menu Shadow</span>
                                    </label>
                                </div>
                                
                                {/* ✅ NEW: Shadow Customization */}
                                {navigationData.mobileMenuShadowEnabled && (
                                    <>
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Shadow Blur
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuShadowBlur || 12}px
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="30"
                                                value={navigationData.mobileMenuShadowBlur || 12}
                                                onChange={(e) => onUpdate({ mobileMenuShadowBlur: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                        
                                        <div className="nav-slider-group">
                                            <label className="nav-slider-label">
                                                Shadow Opacity
                                                <span className="nav-slider-value">
                                                    {navigationData.mobileMenuShadowOpacity || 15}%
                                                </span>
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="50"
                                                value={navigationData.mobileMenuShadowOpacity || 15}
                                                onChange={(e) => onUpdate({ mobileMenuShadowOpacity: parseInt(e.target.value) })}
                                                className="nav-slider"
                                            />
                                        </div>
                                    </>
                                )}
                                
                                {/* ✅ NEW: Border Radius */}
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Menu Border Radius
                                        <span className="nav-slider-value">
                                            {navigationData.mobileMenuBorderRadius || 0}px
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="30"
                                        value={navigationData.mobileMenuBorderRadius || 0}
                                        onChange={(e) => onUpdate({ mobileMenuBorderRadius: parseInt(e.target.value) })}
                                        className="nav-slider"
                                    />
                                </div>
                                
                                {/* ✅ NEW: Menu Padding */}
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Menu Padding
                                        <span className="nav-slider-value">
                                            {navigationData.mobileMenuPadding || 20}px
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="40"
                                        value={navigationData.mobileMenuPadding || 20}
                                        onChange={(e) => onUpdate({ mobileMenuPadding: parseInt(e.target.value) })}
                                        className="nav-slider"
                                    />
                                </div>
                                
                                {/* ✅ NEW: Link Spacing */}
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Link Spacing
                                        <span className="nav-slider-value">
                                            {navigationData.mobileMenuLinkSpacing || 4}px
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="16"
                                        value={navigationData.mobileMenuLinkSpacing || 4}
                                        onChange={(e) => onUpdate({ mobileMenuLinkSpacing: parseInt(e.target.value) })}
                                        className="nav-slider"
                                    />
                                </div>
                                
                                <div className="nav-section-divider"></div>
                                
                                {/* ✅ NEW: Hover & Active States */}
                                <div className="nav-section-title">Link Hover Style</div>
                                
                                {/* Link Hover Background Color */}
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Hover Background</span>
                                    <div className="nav-brand-colors">
                                        {/* Transparent Option */}
                                        <button
                                            type="button"
                                            className={`nav-brand-color ${!navigationData.mobileMenuLinkHoverBg || navigationData.mobileMenuLinkHoverBg === 'transparent' ? 'selected' : ''}`}
                                            style={{ 
                                                background: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                                                backgroundSize: '8px 8px',
                                                backgroundPosition: '0 0, 4px 4px'
                                            }}
                                            onClick={() => onUpdate({ mobileMenuLinkHoverBg: 'transparent' })}
                                            title="Transparent"
                                        >
                                            {(!navigationData.mobileMenuLinkHoverBg || navigationData.mobileMenuLinkHoverBg === 'transparent') && (
                                                <i className="fas fa-check"></i>
                                            )}
                                        </button>
                                        
                                        {/* Brand Colors */}
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.mobileMenuLinkHoverBg === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({ mobileMenuLinkHoverBg: brandColor })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.mobileMenuLinkHoverBg === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* ✅ Hover Background Opacity (wenn nicht transparent) */}
                                {navigationData.mobileMenuLinkHoverBg && navigationData.mobileMenuLinkHoverBg !== 'transparent' && (
                                    <div className="nav-slider-group">
                                        <label className="nav-slider-label">
                                            Hover Opacity
                                            <span className="nav-slider-value">
                                                {navigationData.mobileMenuLinkHoverOpacity || 5}%
                                            </span>
                                        </label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            value={navigationData.mobileMenuLinkHoverOpacity || 5}
                                            onChange={(e) => onUpdate({ mobileMenuLinkHoverOpacity: parseInt(e.target.value) })}
                                            className="nav-slider"
                                        />
                                    </div>
                                )}
                                
                                <div className="nav-section-divider"></div>
                                
                                {/* ✅ NEW: Backdrop Customization */}
                                <div className="nav-section-title">Menu Backdrop</div>
                                
                                {/* Backdrop Color */}
                                <div className="nav-color-group">
                                    <span className="nav-color-label">Backdrop Color</span>
                                    <div className="nav-brand-colors">
                                        {/* Black */}
                                        <button
                                            type="button"
                                            className={`nav-brand-color ${!navigationData.mobileMenuBackdropColor || navigationData.mobileMenuBackdropColor === '#000000' ? 'selected' : ''}`}
                                            style={{ backgroundColor: '#000000' }}
                                            onClick={() => onUpdate({ mobileMenuBackdropColor: '#000000' })}
                                            title="Black"
                                        >
                                            {(!navigationData.mobileMenuBackdropColor || navigationData.mobileMenuBackdropColor === '#000000') && (
                                                <i className="fas fa-check"></i>
                                            )}
                                        </button>
                                        
                                        {/* White */}
                                        <button
                                            type="button"
                                            className={`nav-brand-color ${navigationData.mobileMenuBackdropColor === '#FFFFFF' ? 'selected' : ''}`}
                                            style={{ backgroundColor: '#FFFFFF', border: '2px solid #ccc' }}
                                            onClick={() => onUpdate({ mobileMenuBackdropColor: '#FFFFFF' })}
                                            title="White"
                                        >
                                            {navigationData.mobileMenuBackdropColor === '#FFFFFF' && (
                                                <i className="fas fa-check" style={{ color: '#333' }}></i>
                                            )}
                                        </button>
                                        
                                        {/* Brand Colors */}
                                        {Object.values(colors).slice(0, 4).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`nav-brand-color ${navigationData.mobileMenuBackdropColor === brandColor ? 'selected' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={() => onUpdate({ mobileMenuBackdropColor: brandColor })}
                                                title={`Color ${index + 1}`}
                                            >
                                                {navigationData.mobileMenuBackdropColor === brandColor && (
                                                    <i className="fas fa-check"></i>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Backdrop Opacity */}
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Backdrop Opacity
                                        <span className="nav-slider-value">
                                            {navigationData.mobileMenuBackdropOpacity || 50}%
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="0"
                                        max="90"
                                        value={navigationData.mobileMenuBackdropOpacity || 50}
                                        onChange={(e) => onUpdate({ mobileMenuBackdropOpacity: parseInt(e.target.value) })}
                                        className="nav-slider"
                                    />
                                </div>
                                
                                {/* ✅ Animation Speed */}
                                <div className="nav-slider-group">
                                    <label className="nav-slider-label">
                                        Animation Speed
                                        <span className="nav-slider-value">
                                            {navigationData.mobileMenuAnimationSpeed || 300}ms
                                        </span>
                                    </label>
                                    <input
                                        type="range"
                                        min="100"
                                        max="800"
                                        step="50"
                                        value={navigationData.mobileMenuAnimationSpeed || 300}
                                        onChange={(e) => onUpdate({ mobileMenuAnimationSpeed: parseInt(e.target.value) })}
                                        className="nav-slider"
                                    />
                                </div>
                            </>
                        )}
                        
                        <span className="nav-section-label">Mobile Settings</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default NavigationFloatingToolbar;

